import React, { useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap-icons/font/bootstrap-icons.css";
import "../../../../styles/dashboardTheme.css";
import "../../../../styles/managementTheme.css";
import "../styles/docentes.css";
import DocentesSummary from "../components/DocentesSummary";
import DocentesList from "../components/DocentesList";
import DocentesFormModal from "../components/DocentesFormModal";
import CoordinacionTabs from "../../catalogo/components/CoordinacionTabs";
import useDocentesLogic from "../hooks/useDocentesLogic";
import { usePermissions } from "../../../../utils/permissions";

function ModalPin({ datos, onClose }) {
  const copiar = () => {
    navigator.clipboard?.writeText(datos.pin);
  };

  return (
    <div className="modal d-block" style={{ background: "rgba(0,0,0,.5)" }}>
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header border-0 pb-0">
            <h6 className="modal-title fw-bold">
              <i className="bi bi-key-fill text-warning me-2" />
              {datos.esReset ? "PIN regenerado" : "Acceso Campus habilitado"}
            </h6>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body text-center py-4">
            <p className="text-muted small mb-3">
              Docente: <strong>{datos.nombre}</strong>
            </p>
            <div
              className="d-inline-flex align-items-center gap-3 px-4 py-3 rounded-3 border border-warning bg-warning bg-opacity-10 mb-3"
              style={{ fontSize: 32, letterSpacing: 8, fontWeight: 700 }}
            >
              {datos.pin}
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={copiar}
                title="Copiar PIN"
              >
                <i className="bi bi-clipboard" />
              </button>
            </div>
            <p className="text-muted small mb-0">
              <i className="bi bi-clock me-1" />
              Válido por <strong>{datos.expira_horas} horas</strong>. Comunícalo al docente.
            </p>
            <div className="alert alert-warning small mt-3 mb-0 text-start">
              <i className="bi bi-exclamation-triangle me-1" />
              Este PIN se muestra <strong>una sola vez</strong>. Si el docente lo pierde,
              usa el botón "Resetear acceso" para generar uno nuevo.
            </div>
          </div>
          <div className="modal-footer border-0 pt-0">
            <button className="btn btn-primary w-100" onClick={onClose}>
              Entendido
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function DocentesView() {
  const { hasPermission } = usePermissions();
  const canCreateDocente = hasPermission("docentes.crear");
  const canUpdateDocente = hasPermission("docentes.actualizar");
  const canDeleteDocente = hasPermission("docentes.eliminar");

  const {
    docentes,
    loading,
    listError,
    lastUpdated,
    resumen,
    filtro,
    busqueda,
    setBusqueda,
    pagina,
    setPagina,
    totalFiltradas,
    totalPaginas,
    porPagina,
    modal,
    docenteSeleccionado,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    savingNuevo,
    savingEditar,
    deleting,
    fetchDocentes,
    abrirModalNuevo,
    abrirModalEditar,
    abrirModalEliminar,
    cerrarModal,
    handleCrearDocente,
    handleActualizarDocente,
    handleEliminarDocente,
    aplicarFiltro,
    modalPin,
    setModalPin,
    togglingCampus,
    handleCampusToggle,
    handleCampusReset,
    togglingActivo,
    handleToggleActivo,
  } = useDocentesLogic();

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    if (modal || modalPin) {
      document.body.classList.add("modal-open");
    } else {
      document.body.classList.remove("modal-open");
    }
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [modal, modalPin]);

  const lastSync = lastUpdated ? lastUpdated.toLocaleString() : "Sincronización inicial";

  return (
    <section className="management-page management-theme-teal docentes-wrapper">
      <CoordinacionTabs />
      <div className="management-card management-header mb-4">
        <div>
          <p className="management-eyebrow mb-1">Académico</p>
          <h4 className="management-title mb-1">Docentes</h4>
          <p className="text-muted mb-0">
            Administra el equipo docente disponible para los cursos.
          </p>
        </div>
        <div className="d-flex flex-column align-items-end gap-2">
          {canCreateDocente && (
            <button
              type="button"
              className="btn management-btn-primary"
              onClick={abrirModalNuevo}
            >
              <i className="bi bi-plus-circle me-2" />
              Nuevo docente
            </button>
          )}
          <small className="text-muted">Actualizado: {lastSync}</small>
        </div>
      </div>

      <DocentesSummary
        resumen={resumen}
        onQuickFilter={aplicarFiltro}
        activeKey={filtro}
      />

      {listError && (
        <div className="management-alert d-flex align-items-center justify-content-between gap-3 flex-wrap">
          <div>
            <p className="mb-0">{listError}</p>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={fetchDocentes}
          >
            <i className="bi bi-arrow-repeat me-2" />
            Reintentar
          </button>
        </div>
      )}

      <DocentesList
        docentes={docentes}
        loading={loading}
        reload={fetchDocentes}
        onEdit={canUpdateDocente ? abrirModalEditar : undefined}
        onDelete={canDeleteDocente ? abrirModalEliminar : undefined}
        canEdit={canUpdateDocente}
        canDelete={canDeleteDocente}
        canManageCampus={canUpdateDocente}
        togglingCampus={togglingCampus}
        onCampusToggle={handleCampusToggle}
        onCampusReset={handleCampusReset}
        togglingActivo={togglingActivo}
        onToggleActivo={canUpdateDocente ? handleToggleActivo : undefined}
        busqueda={busqueda}
        setBusqueda={setBusqueda}
        pagina={pagina}
        setPagina={setPagina}
        totalFiltradas={totalFiltradas}
        totalPaginas={totalPaginas}
        porPagina={porPagina}
      />

      <DocentesFormModal
        modal={modal}
        formNuevo={formNuevo}
        setFormNuevo={setFormNuevo}
        formEditar={formEditar}
        setFormEditar={setFormEditar}
        savingNuevo={savingNuevo}
        savingEditar={savingEditar}
        deleting={deleting}
        docenteSeleccionado={docenteSeleccionado}
        cerrarModal={cerrarModal}
        handleCrearDocente={handleCrearDocente}
        handleActualizarDocente={handleActualizarDocente}
        handleEliminarDocente={handleEliminarDocente}
        canUpdate={canUpdateDocente}
        canDelete={canDeleteDocente}
      />

      {modalPin && (
        <ModalPin datos={modalPin} onClose={() => setModalPin(null)} />
      )}
    </section>
  );
}
